import {
    APP_BASE_HREF,
    PlatformLocation,
    registerLocaleData,
} from '@angular/common';
import localeJP from '@angular/common/locales/ja';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AnalyticsEnricherProvider } from '@common/analytics/services/analytics-enricher.service';
import {
    ANALYTICS_API_URL,
    ANALYTICS_LOG_TO_CONSOLE,
    ANALYTICS_SEND_TO_TAGMANAGER,
    API_URL, APPLICATION_INSIGHTS_CONNECTION_STRING,
    BUSINESS,
    CANONICAL_COMMUNITY,
    CMS_ALLOW_LINKS,
    CMS_LOCALE,
    CMS_PRODUCT,
    CMS_REGION,
    CMS_URL,
    COOKIE_POLICY_URL,
    DEFAULT_COMMUNITY,
} from '@common/app/app.config';
import { RouteService } from '@common/infrastructure/params-service';
import { PendingChangesGuard } from '@common/infrastructure/pending-changes.guard';
import { AuthService } from '@common/services/auth.service';
import { CommandService } from '@common/services/command.service';
import { CommunityService } from '@common/services/community.service';
import { ContentService } from '@common/services/content.service';
import { GoogleAnalyticsService } from '@common/services/google-analytics.service';
import { InMemoryAuthStorage } from '@common/services/in-memory-auth-storage';
import { InMemoryQueryService } from '@common/services/inmemory.query.service';
import { KenalyticsService } from '@common/services/kenalytics.service';
import { LinkService } from '@common/services/link.service';
import { EnJpMonitoringService } from './services/monitoring.service';
import { SeoService } from '@common/services/seo.service';
import { ThemeService } from '@common/services/theme.service';
import { VwoStoreService } from '@common/services/vwo-store.service';
import { WINDOW_PROVIDERS } from '@common/services/window.service';
import { ADDRESS_SERVICE_URL } from '@lang/jp/app.config';
import { JapanAddressService } from '@lang/jp/services/japan-address.service';
import { PhaseJpService } from '@lang/jp/services/phasejp.service';
import {
    AuthConfig,
    OAuthModule,
    OAuthModuleConfig,
    OAuthStorage,
    ValidationHandler,
} from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { CounterModule } from './components/counter/counter.module';
import { FooterComponent } from './components/footer/footer.component';
import { HomeComponent } from './components/home/home.component';
import { MenuComponent } from './components/menu/menu.component';
import {
    CHECK_DUPLICATED_REGISTRATIONS,
    PROPOSE_ALTERNATE_COMMUNITIES,
    SHOW_LOCATION_SELECTORS,
} from './enjp.config';
import { CommunityGuard } from './guards/community.guard';
import { EnjpCommunityService } from './services/community.service';
import { ContentHelperService } from './services/contenthelper.service';
import { EnJpContentService } from './services/enjp.content.service';
import { ErrorHandlerService } from './services/error-handler.service';
import { RegistrationService } from './services/registration.service';
import { SharedModule } from './shared.module';
import { AppRoutingModule } from './app-routing.module';

import { AppConfigService, APP_CONFIG } from './services/app-config.service';
import { AppConfig } from './model/app-config';
import {
    authConfigFactory,
    getBaseHref,
    getDefaultCommunity,
} from './services/app-module-helpers';
import { MonitoringService } from "@common/services/monitoring.service";

registerLocaleData(localeJP, 'ja-JP');

@NgModule({
    imports: [
        BrowserModule,
        SharedModule,
        OAuthModule.forRoot(),
        CounterModule,
        AppRoutingModule,
    ],
    declarations: [AppComponent, MenuComponent, FooterComponent, HomeComponent],
    providers: [
        {
            provide: AppConfigService,
            useFactory: (config: AppConfig) => new AppConfigService(config),
            deps: [APP_CONFIG],
        },
        ContentHelperService,
        EnjpCommunityService,
        { provide: CommunityService, useExisting: EnjpCommunityService },
        RouteService,
        LinkService,
        CommandService,
        RegistrationService,
        EnJpContentService,
        { provide: ContentService, useExisting: EnJpContentService },
        PendingChangesGuard,
        GoogleAnalyticsService,
        SeoService,
        AuthService,
        KenalyticsService,
        InMemoryQueryService,
        ThemeService,
        CommunityGuard,
        {
            provide: APPLICATION_INSIGHTS_CONNECTION_STRING,
            useFactory: () => environment.aiConnectionString,
            deps: [AppConfigService]
        },
        {
            provide: MonitoringService, useClass: EnJpMonitoringService,
            deps: [BUSINESS, APPLICATION_INSIGHTS_CONNECTION_STRING],
        },
        JapanAddressService,
        VwoStoreService,
        PhaseJpService,
        WINDOW_PROVIDERS,
        { provide: LOCALE_ID, useValue: 'ja-JP' },
        {
            provide: API_URL,
            useFactory: () => environment.apiUri,
            deps: [AppConfigService],
        },
        { provide: CMS_URL, useValue: 'https://content-jp.ichoosr.com/api/' },
        {
            provide: CMS_ALLOW_LINKS,
            useFactory: () => environment.allowLinksToCms,
            deps: [AppConfigService],
        },
        { provide: CMS_PRODUCT, useValue: 'energy' },
        { provide: CMS_REGION, useValue: 'jp' },
        { provide: CMS_LOCALE, useValue: 'ja' },
        { provide: BUSINESS, useValue: 'enjp' },
        { provide: CANONICAL_COMMUNITY, useValue: null },
        { provide: OAuthStorage, useClass: InMemoryAuthStorage },
        { provide: ValidationHandler, useClass: JwksValidationHandler },
        {
            provide: AuthConfig,
            useFactory: authConfigFactory,
            deps: [PlatformLocation, AppConfigService],
        },
        {
            provide: OAuthModuleConfig,
            useFactory: (): OAuthModuleConfig => {
                return {
                    resourceServer: {
                        allowedUrls: [environment.apiUri],
                        sendAccessToken: true,
                    },
                };
            },
            deps: [AppConfigService],
        },
        {
            provide: ANALYTICS_API_URL,
            useFactory: () => environment.analyticsUri,
            deps: [AppConfigService],
        },
        {
            provide: COOKIE_POLICY_URL,
            useValue:
                'https://consent.cookiebot.com/becd820c-b8b2-431d-9396-d5427db44269/cd.js',
        },
        {
            provide: DEFAULT_COMMUNITY,
            useFactory: getDefaultCommunity,
            deps: [PlatformLocation],
        },
        {
            provide: ADDRESS_SERVICE_URL,
            useFactory: () => environment.addressServiceUri,
            deps: [AppConfigService],
        },
        {
            provide: CHECK_DUPLICATED_REGISTRATIONS,
            useFactory: () => environment.checkDuplicatedRegistrations,
            deps: [AppConfigService],
        },
        {
            provide: PROPOSE_ALTERNATE_COMMUNITIES,
            useFactory: () => environment.proposeAlternateCommunities,
            deps: [AppConfigService],
        },
        {
            provide: SHOW_LOCATION_SELECTORS,
            useFactory: () => environment.showLocationSelectors,
            deps: [AppConfigService],
        },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation],
        },
        {
            provide: AnalyticsEnricherProvider,
            deps: [RegistrationService, EnjpCommunityService],
        },
        {
            provide: ANALYTICS_SEND_TO_TAGMANAGER, // should be removed when fully moved to GA4
            useValue: false,
        },
        {
            provide: ANALYTICS_LOG_TO_CONSOLE, // should be removed when fully moved to GA4
            useFactory: () => !environment.production,
            deps: [AppConfigService],
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
